import { useParams, Link } from 'react-router-dom';
import { SignUp } from '@clerk/clerk-react';
import { useTenantBranding } from '../services/hooks/useTenants';

/**
 * Destino de las invitaciones de Clerk (alta de cliente en el portal).
 *
 * Aceptar una invitación es un flujo de **registro**, no de login: el enlace
 * lleva un `__clerk_ticket` que solo consume `<SignUp>`. Antes las invitaciones
 * apuntaban a `/:tenantCode/login`, que monta `<SignIn>`, así que Clerk no podía
 * completar el alta en la app y dejaba al cliente en su Account Portal (la
 * issuer URL) después de poner la contraseña.
 *
 * Tras registrarse redirigimos a `/:tenantCode`, que ya resuelve el destino por
 * rol: `ProtectedOperatorRoute` manda a los clientes a `mi-calendario` y al
 * resto al dashboard, conservando la sucursal de la URL.
 */
export default function AcceptInvitationPage() {
    const { tenantCode } = useParams<{ tenantCode: string }>();
    const { data: branding } = useTenantBranding(tenantCode ?? '');

    const primaryColor = branding?.metadata.colors?.primary || '#111827';
    const logo = branding?.metadata.logo;

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 gap-6 px-4">
            <div className="flex flex-col items-center gap-3 text-center">
                {logo && (
                    <img src={logo} alt={`Logo ${branding?.name}`} className="h-16 w-auto object-contain" />
                )}
                {branding?.name && (
                    <h1 className="text-xl font-semibold text-gray-800">{branding.name}</h1>
                )}
                <p className="text-sm text-gray-500">Crea tu contraseña para acceder a tu portal.</p>
            </div>

            <SignUp
                path={`/${tenantCode}/accept-invitation`}
                routing="path"
                signInUrl={`/${tenantCode}/login`}
                forceRedirectUrl={`/${tenantCode}`}
                appearance={{ variables: { colorPrimary: primaryColor } }}
            />

            <Link to={`/${tenantCode}/login`} className="text-sm text-blue-600 hover:underline">
                Ya tengo cuenta — iniciar sesión
            </Link>
        </div>
    );
}
